/**
 * Compact signer-form controls: chip toggles (days / multi-select) and
 * “Other” text reveals. Segmented radios are native inputs styled in CSS.
 */

export function parseList(value: string): string[] {
  return value.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
}

export function joinList(values: string[]): string {
  return values.join(", ");
}

function hiddenFor(form: HTMLFormElement, name: string): HTMLInputElement | null {
  const el = form.elements.namedItem(name);
  return el instanceof HTMLInputElement ? el : null;
}

function setChipPressed(row: HTMLElement, selected: Set<string>): void {
  row.querySelectorAll<HTMLButtonElement>(".chip").forEach((btn) => {
    const on = selected.has(btn.dataset.value ?? "");
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.classList.toggle("is-on", on);
  });
}

function revealOthers(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLElement>("[data-other-for]").forEach((wrap) => {
    const name = wrap.dataset.otherFor ?? "";
    const trigger = wrap.dataset.otherWhen ?? "Other";
    const control = form.elements.namedItem(name);
    let current = "";
    if (control instanceof RadioNodeList || control instanceof HTMLSelectElement) {
      current = control.value;
    } else if (control instanceof HTMLInputElement) {
      current = control.value;
    }
    const on = parseList(current).includes(trigger) || current === trigger;
    wrap.classList.toggle("hidden", !on);
  });
}

/** Reflect hidden/select values onto chips and Other text visibility. */
export function syncPracticeControls(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLElement>("[data-chips]").forEach((row) => {
    const hidden = hiddenFor(form, row.dataset.chips ?? "");
    if (!hidden) return;
    setChipPressed(row, new Set(parseList(hidden.value)));
  });
  revealOthers(form);
}

/** Wire chip rows (click toggles write a comma-separated hidden input). */
export function initPracticeControls(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLElement>("[data-chips]").forEach((row) => {
    row.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button.chip");
      if (!(btn instanceof HTMLButtonElement) || !row.contains(btn)) return;
      const hidden = hiddenFor(form, row.dataset.chips ?? "");
      if (!hidden) return;
      const value = btn.dataset.value ?? "";
      const selected = new Set(parseList(hidden.value));
      if (selected.has(value)) selected.delete(value);
      else selected.add(value);
      hidden.value = joinList([...selected]);
      hidden.dispatchEvent(new Event("change", { bubbles: true }));
      setChipPressed(row, selected);
      revealOthers(form);
    });
  });

  form
    .querySelector<HTMLSelectElement>('[name="practiceSoftware"]')
    ?.addEventListener("change", () => revealOthers(form));

  syncPracticeControls(form);
}
